import Aegis from 'aegis-web-sdk';
import { getTencentCaptchaKey } from '@/services/authority/login';
import { Message } from '@bluevision/components';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { aegis } from '@/global';
/** APP_ID */
const CAPTCHA_APP_ID = '191573373';

interface ICaptchaResult {
  ret: number;
  ticket: string;
  randstr: string;
  CaptchaAppId?: string;
  bizState?: string;
  errorCode?: number;
  errorMessage?: string;
}

export type CaptchaParams = Partial<Pick<ICaptchaResult, 'ticket' | 'randstr'>>;

interface UseCaptchaOptions {
  callFn?: ((captchaParams: CaptchaParams) => void) | null;
  userEmail?: string; // 用户输入的邮箱
}

export default function useCaptcha(
  options: UseCaptchaOptions | ((captchaParams: CaptchaParams) => void) | null,
) {
  // 兼容旧的调用方式
  const { callFn, userEmail } =
    typeof options === 'function' ? { callFn: options, userEmail: undefined } : options || {};
  const { t, i18n } = useTranslation();
  // 滑块回调
  const captchaCallback = useCallback(
    async (res: ICaptchaResult) => {
      const { ticket, randstr } = res;
      if (ticket && randstr) {
        try {
          /**
           * 旧逻辑：1. 调用单独的校验验证码接口(参数：ticket、randstr)，校验成功后调取业务接口。不安全，可跳过校验。
           * 新逻辑：1. 不单独调用校验验证码，直接将ticket和randstr传给业务接口，业务接口来校验。
           */
          callFn?.({ ticket, randstr });
        } catch (e) {
          Message.error(t('failed_to_obtain_the_verification_code'));
        }
      }
    },
    [t, callFn],
  );
  // 加载失败
  const captchaLoadErrorCallback = useCallback(() => {
    const errorCode = 1001;
    const errorMessage = 'jsload_error';
    const ticket = 'trerror_1001_' + CAPTCHA_APP_ID + '_' + Math.floor(new Date().getTime() / 1000);

    // 滑块加载失败时上报错误信息
    if (aegis) {
      const ext1: Record<string, unknown> = {
        captchaAppId: CAPTCHA_APP_ID,
        errorType: '滑块加载失败',
        errorCode,
        errorMessage,
        userEmail: userEmail || '未获取',
        userAgent: navigator.userAgent,
      };
      const ext2: Record<string, unknown> = {
        frontendError: '腾讯滑块验证码JS加载失败',
        ticket,
        language: i18n.language,
      };
      aegis.report({
        msg: '滑块验证码加载失败',
        level: Aegis.logType.EVENT,
        ext1: JSON.stringify(ext1),
        ext2: JSON.stringify(ext2),
        trace: 'trace',
      });
    }

    captchaCallback({
      ret: 0,
      randstr: '@' + Math.random().toString(36).substr(2),
      ticket: ticket,
      errorCode,
      errorMessage,
    });
  }, [captchaCallback, userEmail, i18n.language]);

  // 开启滑块验证
  const onCaptchaShow = useCallback(
    (encryptData: string) => {
      try {
        // @ts-expect-error TencentCaptcha无法被识别
        const captcha = new TencentCaptcha(CAPTCHA_APP_ID, captchaCallback, {
          aidEncrypted: encryptData,
          userLanguage: i18n.language,
        });
        captcha.show();
      } catch (error) {
        // 滑块初始化失败时上报错误信息
        if (aegis) {
          const ext1: Record<string, unknown> = {
            captchaAppId: CAPTCHA_APP_ID,
            userEmail: userEmail || '未获取',
            userAgent: navigator.userAgent,
          };
          const ext2: Record<string, unknown> = {
            frontendError: '腾讯滑块验证码初始化异常',
            error: error,
            language: i18n.language,
          };

          aegis.report({
            msg: '滑块验证码初始化失败',
            level: Aegis.logType.EVENT,
            ext1: JSON.stringify(ext1),
            ext2: JSON.stringify(ext2),
            trace: 'trace',
          });
        }

        captchaLoadErrorCallback();
      }
    },
    [captchaLoadErrorCallback, captchaCallback, i18n.language, userEmail],
  );

  const captchaInit = async () => {
    const [data, errorInfo] = await getTencentCaptchaKey();
    if (errorInfo) {
      Message.error(errorInfo);
      return;
    }
    onCaptchaShow(data?.aidEncrypted as string);
  };

  return {
    captchaInit,
  };
}
